import { Component, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { combineLatest, Observable, of, map, startWith, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

import { FetchPriorityEnum } from '@fcom/ui-components/components/images/enums';
import { LanguageService } from '@fcom/ui-translate';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { finShare, safeMap } from '@fcom/rx';
import { isBlank } from '@fcom/core/utils/utils';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import * as GlobalBookingActions from '@fcom/common/store/actions/global-booking.actions';
import { AppState, CMSDiscountCode, GlobalBookingTravelClass } from '@fcom/core';

import { BlockOfferData, BlockOffer } from '../../interfaces/destination-promo.interface';
import { MarketingOffer } from '../../interfaces/marketing-offer.interface';
import { CheapestPriceForAllDestinationsService } from '../../services/cheapest-price/cheapest-price-for-all-destinations.service';

@Component({
  selector: 'fin-block-offer',
  templateUrl: './block-offer.component.html',
  standalone: false,
})
export class BlockOfferComponent {
  private languageService = inject(LanguageService);
  private store$ = inject<Store<AppState>>(Store);
  private gtmService = inject(GtmService);
  private cheapestPriceForAllDestinationsService = inject(CheapestPriceForAllDestinationsService);

  destinationClicked = output<[BlockOffer, GlobalBookingTravelClass]>();
  flightHotelClicked = output<{ amLink: string; destinationTitle: string; destinationCode: string }>();
  calendarClicked = output<[Event, BlockOffer, GlobalBookingTravelClass, TripType]>();

  tripType = input(TripType.RETURN, {
    transform: (value: TripType) => {
      return value ?? TripType.RETURN;
    },
  });

  offer = input<BlockOfferData>();

  fetchImagePriority = input<FetchPriorityEnum>();

  showPriceCalendarButton = input(true);

  private readonly offer$ = toObservable(this.offer);

  private readonly countryName$ = this.offer$.pipe(
    switchMap((offer) => {
      return offer ? this.countryName(offer) : of(undefined);
    }),
    finShare()
  );

  readonly blockOffer = toSignal(
    combineLatest([this.offer$, toObservable(this.tripType), this.countryName$]).pipe(
      switchMap(([offer, tripType, countryName]) => {
        if (!offer) {
          return of({} as BlockOffer);
        }

        return this.marketingOffer(offer.destination, tripType).pipe(
          map((marketingOffer) => {
            return {
              ...this.setUpPrices(offer, marketingOffer, tripType),
              countryName,
              marketingOffer,
            };
          })
        );
      })
    )
  );

  emitCalendarClick(
    [event, travelClass, tripType]: [Event, GlobalBookingTravelClass, TripType],
    blockOffer: BlockOffer
  ): void {
    this.calendarClicked.emit([event, blockOffer, travelClass, tripType]);
  }

  private countryName({ flag }: BlockOfferData): Observable<string> {
    if (isBlank(flag)) {
      return of(undefined);
    }
    return this.languageService.translate<Array<{ countryCode?: string; name?: string }>>('countryCodes').pipe(
      map((countries: Array<{ countryCode?: string; name?: string }>) =>
        countries.find((country) => country.countryCode === flag.toUpperCase())
      ),
      safeMap(({ name }) => name),
      startWith(undefined as string)
    );
  }

  private marketingOffer(destination: string, tripType: TripType): Observable<MarketingOffer> {
    return this.cheapestPriceForAllDestinationsService.offerFor(destination, tripType).pipe(
      map((offer: MarketingOffer) => {
        return {
          ...offer,
          prices: offer.prices.filter(Boolean),
        };
      }),
      startWith(undefined as MarketingOffer)
    );
  }

  private setUpPrices(offer: BlockOfferData, marketingOffer: MarketingOffer, tripType: TripType): BlockOfferData {
    const priceOffer = marketingOffer?.prices.find((price) => price.travelClass === GlobalBookingTravelClass.ECONOMY);

    if (!priceOffer) {
      return offer;
    }

    const onewayPrice = tripType === TripType.ONEWAY ? priceOffer : undefined;
    const returnPrice = tripType === TripType.RETURN ? priceOffer : undefined;

    return { ...offer, onewayPrice, returnPrice };
  }

  onLinkClick([travelClass]: [GlobalBookingTravelClass, TripType]): void {
    this.destinationClicked.emit([this.offer(), travelClass]);
    this.prefillBookingWidget(travelClass);
  }

  prefillBookingWidget(travelClass: GlobalBookingTravelClass, discountCode?: CMSDiscountCode): void {
    const offerDestination = this.blockOffer()?.destination;
    const tripType = this.tripType();
    if (offerDestination) {
      this.store$.dispatch(GlobalBookingActions.setTripType({ tripType }));
      this.store$.dispatch(
        GlobalBookingActions.setTravelClass({
          travelClass: travelClass === GlobalBookingTravelClass.BUSINESS ? travelClass : GlobalBookingTravelClass.MIXED,
        })
      );

      if (discountCode?.code) {
        this.store$.dispatch(GlobalBookingActions.setDiscountCode({ discountCode: discountCode.code }));
      }

      this.gtmService.trackElement(
        `tile-${tripType.toLowerCase()}-${travelClass.toLowerCase()}`,
        GaContext.BLOCK_OFFER,
        ElementTypes.BANNER,
        `${offerDestination}`,
        ElementActions.CLICK
      );
    }
  }
}
